import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { availableTools } from '@/lib/tools';

interface ToolPickerProps {
  selectedTools: string[];
  onToolSelect: (toolId: string) => void;
}

const ToolPicker = ({ selectedTools, onToolSelect }: ToolPickerProps) => {
  const [activeCategory, setActiveCategory] = useState<string>('No-Code');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Get all categories from tools
  const categories = Array.from(new Set(availableTools.map(tool => tool.category)));
  
  // Function to filter tools based on search term or active category
  const getFilteredTools = () => {
    const toolsToFilter = availableTools;
    
    if (searchTerm.trim()) {
      return toolsToFilter.filter(tool => 
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return toolsToFilter.filter(tool => tool.category === activeCategory);
  };
  
  const filteredTools = getFilteredTools();
  
  return (
    <div className="vibebops-card w-80 max-h-96 p-3 overflow-hidden flex flex-col bg-vibebops-card border border-vibebops-border rounded-lg">
      {/* Search Bar */}
      <div className="relative mb-3 sticky top-0 z-10">
        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-vibebops-textMuted" />
        </div>
        <Input 
          type="text"
          placeholder="Search tools & technologies..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 bg-vibebops-highlight/20 border-vibebops-border text-vibebops-text"
        />
      </div>
      
      {/* Category tabs - hide when searching */}
      {!searchTerm && (
        <div className="flex flex-wrap gap-1.5 mb-3 overflow-visible">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-2 py-1.5 text-xs rounded-md whitespace-nowrap mb-1 ${
                activeCategory === category 
                  ? 'bg-vibebops-accent text-white font-medium' 
                  : 'bg-vibebops-highlight/80 text-vibebops-textMuted hover:bg-vibebops-highlight'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      )}
      
      {/* Tools Grid */}
      <div className="overflow-y-auto pr-1 flex-grow">
        <div className="grid grid-cols-2 gap-2">
          {filteredTools.map((tool) => (
            <div 
              key={tool.id}
              onClick={() => onToolSelect(tool.id)}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                selectedTools.includes(tool.id) 
                  ? 'bg-vibebops-accent text-white' 
                  : 'hover:bg-vibebops-highlight text-vibebops-text'
              }`}
            >
              <div className="flex items-center justify-center h-6 w-6 bg-opacity-20 rounded">
                <tool.icon className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium truncate">{tool.name}</span>
            </div>
          ))}
        </div>
        
        {filteredTools.length === 0 && (
          <div className="text-center py-6 text-vibebops-textMuted">
            No tools found
          </div>
        )}
      </div>
      
      {/* Selected Tools */}
      {selectedTools.length > 0 && (
        <div className="pt-2 mt-2 border-t border-vibebops-border">
          <div className="text-xs text-vibebops-textMuted mb-2">Selected Tools:</div>
          <div className="flex flex-wrap gap-1">
            {selectedTools.map(toolId => {
              const tool = availableTools.find(t => t.id === toolId);
              if (!tool) return null;
              
              return (
                <Badge 
                  key={toolId} 
                  variant="secondary"
                  className="flex items-center gap-1 py-1 px-2 cursor-pointer bg-vibebops-highlight/80 text-vibebops-text"
                  onClick={() => onToolSelect(toolId)}
                >
                  <tool.icon className="h-3 w-3" />
                  <span>{tool.name}</span>
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolPicker; 